// SQL/PGQ GRAPH_TABLE slot classifier — the fifth token-scan recognizer family
// on the EditorContext slot seam (O2 / Task 18). PRD: SQL/PGQ editor support.
// G11: pure text scan over the tokenised statement slice, NO web-tree-sitter —
// the legal projection-side body the future tree-sitter swap replaces behind the
// identical signature. Keys on the same GRAPH_TABLE surface both grammar forks
// (Oracle + PG19) share; dispatched on `GRAPH_TABLE_ENGINES` from
// `editorContextSlot.ts`.
//
// The classifier answers "which GPML/COLUMNS slot does the cursor sit in inside
// an unclosed `GRAPH_TABLE ( … `?" and returns null for everything else (so it
// refines a graph position and never touches non-PGQ SQL — a statement with no
// `GRAPH_TABLE (` token classifies to null immediately). The four slots:
//   - graph-name             — `GRAPH_TABLE( ▮` / `GRAPH_TABLE(my_gr▮` (only ws
//     since `(`, or an *attached* ident prefix still being typed). A COMPLETE,
//     detached name (`GRAPH_TABLE(g ▮`) is NOT graph-name — the next legal token
//     there is MATCH, which the resolvers surface (classify returns null).
//   - graph-label            — `… IS ▮` / after `|`/`&`/`!` / `IS LABELED ▮`
//   - graph-element-property — `elem.▮` inside a MATCH pattern / WHERE / COLUMNS
//   - graph-columns          — inside an unclosed `COLUMNS ( ▮`
//
// `collectGraphElementVariables` is the inventory helper Task 19/20 consumes to
// bind element variables to their declared labels for downstream column /
// property candidates.

import type { ClauseToken } from './editorContextClause';
import { tokenizeUntil } from './editorContextClause';
import { findEnclosingLparenIndex, upper } from './editorContextSlotShared';

/** The engines whose grammar has `GRAPH_TABLE` (SQL/PGQ). Both forks share the
 *  identical GPML surface, so the token classifier is engine-neutral and this
 *  set is the single dispatch gate `resolveSlotKind` consults. mssql/mysql/
 *  sqlite are absent — they have no `GRAPH_TABLE`. */
export const GRAPH_TABLE_ENGINES: ReadonlySet<string> = new Set(['oracle', 'postgres']);

export type GraphSlotKind =
  | 'graph-name'
  | 'graph-label'
  | 'graph-element-property'
  | 'graph-columns';

/** Result of `classifyGraphSlot`. `elementVariable` is populated only for the
 *  `graph-element-property` arm (the identifier left of the dot). The seam
 *  (`resolveSlotKind`) consumes only `.kind`; the variable also reaches Task 20
 *  via the projection's existing `dottedBase`. */
export type GraphSlot = {
  readonly kind: GraphSlotKind;
  readonly elementVariable?: string;
};

/** One element variable declared in a MATCH pattern with its label list. */
export type GraphElementVar = {
  readonly variable: string;
  readonly labels: readonly string[];
};

/**
 * Classify the GRAPH_TABLE slot the cursor sits in, or null when the cursor is
 * not inside an unclosed `GRAPH_TABLE ( … `. `cursorOffset` distinguishes a
 * still-typing keyword tail (`IS▮`) from a separated slot (`IS ▮`) — matching
 * the ddl2 family's attached-tail convention.
 */
export function classifyGraphSlot(
  tokens: readonly ClauseToken[],
  cursorOffset: number,
): GraphSlot | null {
  const graphLparen = findGraphTableLparenIndex(tokens);
  if (graphLparen < 0) return null;

  // Graph-name region — only whitespace + an optional identifier prefix since
  // the `(`, i.e. before the MATCH keyword and before any structural token.
  // (Runs first so a bare `GRAPH_TABLE(▮` never falls through to a content
  // rule; a dotted/qualified name is intentionally NOT graph-name per the
  // brief's "only ws/ident-prefix since (" definition.)
  if (onlyGraphNameSince(tokens, graphLparen, cursorOffset)) return { kind: 'graph-name' };

  // The content rules (property / label / columns) only apply once MATCH has
  // opened the pattern body — this keeps them out of the pre-MATCH graph-name
  // region (e.g. a schema-qualified name `GRAPH_TABLE(s.▮` stays null → normal
  // dotted-access handling, not a bogus element-property).
  if (!hasMatchSince(tokens, graphLparen)) return null;

  // Element property `elem.▮` — highest-priority content rule so it wins inside
  // a COLUMNS paren too (a `graph-columns` position with a dot is a property).
  const elementVariable = graphElementPropertyVariable(tokens);
  if (elementVariable !== null) return { kind: 'graph-element-property', elementVariable };

  // Label expression `IS ▮` / `… |▮` / `IS LABELED ▮` / `IS (▮`.
  if (followsGraphLabel(tokens, cursorOffset)) return { kind: 'graph-label' };

  // Anything else inside an unclosed `COLUMNS (` is a projected-column slot.
  if (insideColumnsParen(tokens)) return { kind: 'graph-columns' };

  return null;
}

// ---------------------------------------------------------------------------
// Enclosing `GRAPH_TABLE (` discovery
// ---------------------------------------------------------------------------

/**
 * Index of the `(` of the nearest unclosed `GRAPH_TABLE (` enclosing the
 * cursor, or -1. Walks back over a balanced paren stack, inspecting EVERY
 * enclosing (unclosed) `(` from innermost outward — the graph paren sits
 * outside the node/edge-pattern and COLUMNS parens the cursor may also be in.
 */
function findGraphTableLparenIndex(tokens: readonly ClauseToken[]): number {
  let depth = 0;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.kind === 'rparen') {
      depth += 1;
    } else if (t.kind === 'lparen') {
      if (depth === 0) {
        // An enclosing (unclosed) `(` — is it the GRAPH_TABLE one?
        if (upper(tokens[i - 1]) === 'GRAPH_TABLE') return i;
        // Otherwise it is an inner node/edge/COLUMNS paren; keep walking out.
      } else {
        depth -= 1;
      }
    }
  }
  return -1;
}

/** True when only whitespace + at most one *still-being-typed* identifier
 *  prefix separates the cursor from the `GRAPH_TABLE (` (the graph-name slot).
 *  A COMPLETE, detached name (`GRAPH_TABLE(g ▮`, the ident's end < cursor) is
 *  NOT graph-name: the cursor has moved past the name and the next legal token
 *  is MATCH (surfaced by the resolver), so we fall through to null. Uses the
 *  same attached-tail convention as `followsGraphLabel` (last token's end <
 *  cursorOffset ⇒ detached). */
function onlyGraphNameSince(
  tokens: readonly ClauseToken[],
  graphLparen: number,
  cursorOffset: number,
): boolean {
  const after = tokens.length - 1 - graphLparen;
  if (after === 0) return true; // nothing after `(` — still expecting the name
  if (after === 1 && tokens[graphLparen + 1]!.kind === 'identifier') {
    // Attached tail (`GRAPH_TABLE(my_gr▮`, ident.end === cursor) → still typing
    // the name. Detached (`GRAPH_TABLE(g ▮`, ident.end < cursor) → complete.
    return tokens[graphLparen + 1]!.end >= cursorOffset;
  }
  return false;
}

/** True when a `MATCH` keyword-lexed token appears after the graph paren. */
function hasMatchSince(tokens: readonly ClauseToken[], graphLparen: number): boolean {
  for (let i = graphLparen + 1; i < tokens.length; i += 1) {
    if (upper(tokens[i]) === 'MATCH') return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Content-slot recognizers
// ---------------------------------------------------------------------------

/** `elem.▮` / `elem.par▮` at the tail → the element variable (`elem`), else
 *  null. Multi-part `a.b.▮` yields the identifier left of the final dot. */
function graphElementPropertyVariable(tokens: readonly ClauseToken[]): string | null {
  let i = tokens.length - 1;
  if (i >= 0 && tokens[i]!.kind === 'identifier') i -= 1; // trailing partial property
  if (i < 0 || tokens[i]!.kind !== 'dot') return null;
  i -= 1;
  if (i < 0 || tokens[i]!.kind !== 'identifier') return null;
  return tokens[i]!.text;
}

/** True when the cursor sits in a label-expression position. */
function followsGraphLabel(tokens: readonly ClauseToken[], cursorOffset: number): boolean {
  const n = tokens.length;
  if (n === 0) return false;
  const last = tokens[n - 1]!;

  // Parenthesised label group `IS (▮` / `… |(▮`: the `(` is a label opener when
  // its preceding token is IS/LABELED or a label combinator.
  if (last.kind === 'lparen') {
    const before = tokens[n - 2];
    return upper(before) === 'IS' || upper(before) === 'LABELED' || isLabelOp(before);
  }

  // Bare IS/LABELED lead directly at the cursor — require whitespace separation
  // so a still-typing `IS▮` does not fire (it would hide the keyword's own
  // completion), matching the ddl2 attached-tail rule.
  const lastU = upper(last);
  if (lastU === 'IS') return last.end < cursorOffset && isLabelPredicateIs(tokens, n - 1);
  if (lastU === 'LABELED') return last.end < cursorOffset;

  // Otherwise walk back over label-expression tokens (partial label idents +
  // `|`/`&`/`!` combinators) to the lead. A dot / keyword / other operator
  // breaks the scan, so `a.x | ▮` (bitwise, dotted) and `WHERE x = ▮` do NOT
  // mis-classify as labels.
  for (let i = n - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    const u = upper(t);
    if (u === 'IS') return isLabelPredicateIs(tokens, i);
    if (u === 'LABELED') return true;
    if (isLabelExprToken(t)) continue;
    return false;
  }
  return false;
}

/** `IS` is a label predicate (`elem IS label`) rather than a null-test
 *  (`expr.col IS NULL`) unless the token before it is a dotted-access tail. */
function isLabelPredicateIs(tokens: readonly ClauseToken[], isIdx: number): boolean {
  const prev = tokens[isIdx - 1];
  if (prev && prev.kind === 'identifier' && tokens[isIdx - 2]?.kind === 'dot') return false;
  return true;
}

/** A token that can appear inside a label expression: an identifier (label
 *  name / partial) or a `|`/`&`/`!` combinator. Keywords are boundaries. */
function isLabelExprToken(t: ClauseToken | undefined): boolean {
  if (!t) return false;
  return t.kind === 'identifier' || isLabelOp(t);
}

/** An operator token composed only of the label combinators `|`, `&`, `!`. */
function isLabelOp(t: ClauseToken | undefined): boolean {
  if (!t || t.kind !== 'operator' || t.text.length === 0) return false;
  for (const ch of t.text) {
    if (ch !== '|' && ch !== '&' && ch !== '!') return false;
  }
  return true;
}

/** True when the innermost enclosing `(` is a `COLUMNS (` opener. */
function insideColumnsParen(tokens: readonly ClauseToken[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx < 0) return false;
  return upper(tokens[lparenIdx - 1]) === 'COLUMNS';
}

// ---------------------------------------------------------------------------
// Element-variable inventory (consumed by Task 19/20)
// ---------------------------------------------------------------------------

/**
 * Scan a GRAPH_TABLE body for its declared element variables and their labels
 * — `(x IS lbl…)` node patterns and `-[x IS lbl…]-` edge patterns (the edge
 * brackets are transparent to the Oracle/PG tokenizer, so both reduce to
 * `<var> IS <label…>`). A heuristic inventory: over-collects a bare
 * `WHERE x IS …` predicate, ignores parenthesised label groups, and skips
 * dotted `a.b IS NULL` tests. Returned in declaration order.
 *
 * `engine` selects the tokenizer dialect (defaults to `'postgres'` so existing
 * call sites compile unchanged). The Oracle resolver passes `'oracle'` so an
 * Oracle `q'[…]'` / dollar-free identifier inside a pattern WHERE tokenizes
 * under the right dialect rather than PG's `$$`-aware lexer.
 */
export function collectGraphElementVariables(
  bodyText: string,
  engine: string = 'postgres',
): GraphElementVar[] {
  const tokens = tokenizeUntil(bodyText, 0, bodyText.length, engine);
  const out: GraphElementVar[] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    if (upper(tokens[i]) !== 'IS') continue;
    const varTok = tokens[i - 1];
    if (!varTok || varTok.kind !== 'identifier') continue;
    if (tokens[i - 2]?.kind === 'dot') continue; // dotted expr → not a declaration
    let j = i + 1;
    if (upper(tokens[j]) === 'LABELED') j += 1; // `IS LABELED lbl`
    const labels: string[] = [];
    while (j < tokens.length && tokens[j]!.kind === 'identifier') {
      labels.push(tokens[j]!.text);
      j += 1;
      if (j < tokens.length && isLabelOp(tokens[j])) { j += 1; continue; }
      break;
    }
    out.push({ variable: varTok.text, labels });
  }
  return out;
}
